import { useTranslation } from "react-i18next";

import { HaNContext } from "@lib/logic";
import type { BuildingWorkspaceReservationResponse } from "@mapiq/user-api";
import { timeLabel } from "@lib/store/src/data/timeslots/dateTimeHelpers";
import { useRequiredContext } from "@lib/utils";
import { Icon } from "@atoms";
import { cn } from "@utils";

import { AreaBusynessIcon } from "../../../shared/AreaBusynessIcon";

type PersonBookingCardProps = {
  reservation: BuildingWorkspaceReservationResponse;
  // On during the current refresh bucket, so it's the one the map shows
  isNow: boolean;
  onSelect: () => void;
};

// When the person booked, in which area, and on which floor and desk, with how busy the area's sensors say it is. A
// click shows it on the map.
export const PersonBookingCard = ({ reservation, isNow, onSelect }: PersonBookingCardProps) => {
  const { t } = useTranslation();
  const { filterData } = useRequiredContext(HaNContext);
  const { workspace, localStart, localEnd } = reservation;

  const area = filterData.workspaces.allData.find(a => a.id === workspace.areaId);
  const place = [workspace.floorName, workspace.deskName].filter(Boolean).join(", ");

  return (
    <button
      type="button"
      aria-current={isNow ? "time" : undefined}
      className={cn(
        "flex w-full items-center gap-4 rounded-xl border bg-white p-4 text-left transition-colors hover:border-mapiq-black-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mapiq-black-900",
        { "border-mapiq-black-800* ring-1 ring-mapiq-black-800* hover:border-mapiq-black-800*": isNow },
      )}
      onClick={onSelect}>
      <Icon
        aria-hidden
        className="flex-none self-start"
        icon={workspace.deskId ? "desk" : "area"}
        size="24px"
        color="currentColor"
      />

      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span>{timeLabel(localStart, localEnd, t)}</span>

        <span className="flex flex-col">
          <span className="truncate font-semibold">{workspace.areaName}</span>
          <span className="truncate">{place}</span>
        </span>
      </span>

      {area?.sensorInformation && <AreaBusynessIcon status={area.sensorInformation.status} />}
    </button>
  );
};
